import { cn } from '@/utils/cn';
import { type WalletTransaction } from '@/api/wallet-api';
import { ArrowUpRight, ArrowDownLeft } from 'lucide-react';

interface WalletTransactionTableProps {
  transactions: WalletTransaction[];
  className?: string;
  isLoading?: boolean;
}

export function WalletTransactionTable({
  transactions,
  className,
  isLoading,
}: WalletTransactionTableProps) {
  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className={cn('text-sm text-gray-400 py-8 text-center', className)}>
        Nenhuma transação encontrada
      </div>
    );
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(value));

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  const typeLabels: Record<string, string> = {
    CREDIT: 'Crédito',
    PENDING_CREDIT: 'Crédito Pendente (Consulta)',
    RELEASE: 'Liberação de Saldo',
    DEBIT: 'Débito',
    REFUND: 'Reembolso',
    WITHDRAWAL: 'Saque',
    PAYOUT: 'Saque Realizado',
    FEE: 'Taxa',
  };

  return (
    <div className={cn('space-y-1', className)}>
      {transactions.map((tx) => {
        const isDebitType = tx.type === 'DEBIT' || tx.type === 'WITHDRAWAL' || tx.type === 'PAYOUT' || tx.type === 'FEE';
        const isCredit = tx.amount >= 0 && !isDebitType;
        const typeLabel = typeLabels[tx.type] || tx.type;

        return (
          <div
            key={tx.id}
            className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'p-1.5 rounded-full',
                  isCredit ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                )}
              >
                {isCredit ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {tx.description || typeLabel}
                </p>
                <p className="text-xs text-gray-500">{formatDate(tx.createdAt)}</p>
              </div>
            </div>
            <span
              className={cn(
                'text-sm font-medium',
                isCredit ? 'text-green-600' : 'text-red-600'
              )}
            >
              {isCredit ? '+' : '-'}{formatCurrency(tx.amount)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
